import React, { useMemo } from "react";
import moment from "moment";
import { Tooltip } from "@mui/material";

import { Accordion } from "../../../common/commonComponents";
import ServerDataGrid from "views/pages/manage/common/data-grid/ServerDataGrid";

import { MdOutlineRemoveRedEye } from "react-icons/md";
import { ReactComponent as InfoIcon } from "assets/img/svg/InfoCircleRedFilled.svg";

import CustomChip from "views/pages/manage/common/customChip/CustomChip";
import { numberWithCommas } from "./ConsolidatedFeeDetails";
import { parse2Digit } from "../../../unAssigned/otherCharges/otherChargeDetails/OtherChargeList";
import { getFormattedDate } from "../../../common/utils/methods/commonMethods/utilityMethod";

export const headerColumnBorder = {
  borderBottom: "1px solid #E3E3E3",
  paddingBottom: "5px",
};

export const statusChip = {
  PAID: { text: "Paid", textColor: "#0B442D", bgColor: "#BBE2CE" },
  "NOT PAID": { text: "Not Paid", textColor: "#B32306", bgColor: "#F9D9D7" },
  "PARTIALLY PAID": {
    text: "Partially Paid",
    textColor: "#705010",
    bgColor: "#FFEDC8",
  },
  OVERDUE: {
    text: (
      <span className="cursor">
        Overdue <InfoIcon />
      </span>
    ),
    textColor: "#B32306",
    bgColor: "#F9D9D7",
  },
};

// ✅ TOOLTIP LOGIC (KEPT EXPORT SAFE)
export const getStatusTooltipTitle = (row) => {
  if (row?.status?.toUpperCase() !== "OVERDUE") return "";

  const today = moment();
  const due = moment(row.dueDate);

  if (due < today) {
    const diff = today.diff(due, "days");
    return `Payment of ${row.description} is overdue by ${diff} days.`;
  }
};

export const ColoredChip = ({ value }) => {
  if (!value) return "-";

  const chip = statusChip[value.toUpperCase()] || {};

  return (
    <CustomChip
      bgColor={chip.bgColor}
      textColor={chip.textColor}
      text={chip.text}
      className="w-100"
    />
  );
};

// REUSABLE GRID
const FeeSummaryGrid = ({ rows, openSummaryDialog }) => {
  const getFeeValues = (row) => {
    const list = row?.feeBreakupDTOList || [];

    return list.reduce(
      (acc, item) => {
        acc.base += item.grossAmount || 0;
        acc.concession += item.concessionApplied || 0;
        acc.payable += item.payableAmount || 0;
        acc.tax += item.taxes || 0;
        acc.net += item.netPayable || 0;
        return acc;
      },
      {
        base: 0,
        concession: 0,
        payable: 0,
        tax: 0,
        net: 0,
      },
    );
  };

  const columns = useMemo(
    () => [
      {
        key: "description",
        label: "Item Type",
        width: 180,
        render: (v) => v || "-",
      },
      {
        key: "dueDate",
        label: "Due By",
        width: 130,
        render: (v) => (v ? getFormattedDate(v) : "-"),
      },

      // ✅ NEW
      {
        key: "baseFees",
        label: "Base Fees",
        width: 150,
        render: (v) => `₹ ${numberWithCommas(parse2Digit(v || 0))}`,
      },

      {
        key: "concession",
        label: "Concession",
        width: 150,
        render: (_, row) => {
          const val = getFeeValues(row).concession;
          return `₹ ${numberWithCommas(parse2Digit(val))}`;
        },
      },

      {
        key: "feesPayable",
        label: "Fees Payable",
        width: 160,
        render: (_, row) => {
          const val = getFeeValues(row).payable;
          return `₹ ${numberWithCommas(parse2Digit(val))}`;
        },
      },

      {
        key: "tax",
        label: "Tax",
        width: 120,
        render: (_, row) => {
          const val = getFeeValues(row).tax;
          return `₹ ${numberWithCommas(parse2Digit(val))}`;
        },
      },

      {
        key: "netPayable",
        label: "Net Payable (with Tax)",
        width: 200,
        render: (_, row) => {
          const val = getFeeValues(row).net;
          return `₹ ${numberWithCommas(parse2Digit(val))}`;
        },
      },

      // existing
      {
        key: "paidAmount",
        label: "Amount Paid",
        width: 160,
        render: (v) => `₹ ${numberWithCommas(parse2Digit(v || 0))}`,
      },
      {
        key: "dateOfPayment",
        label: "Paid On",
        width: 140,
        render: (v) => (v ? getFormattedDate(v) : "-"),
      },

      {
        key: "outstandingAmount",
        label: "Balance",
        width: 180,
        render: (v) => `₹ ${numberWithCommas(parse2Digit(v || 0))}`,
      },

      // PINNED STATUS
      {
        key: "status",
        label: "Status",
        width: 130,
        pinned: true,
        render: (value, row) => (
          <Tooltip title={getStatusTooltipTitle(row)}>
            <div className="cursor">
              <ColoredChip value={value} />
            </div>
          </Tooltip>
        ),
      },

      // PINNED ACTION
      {
        key: "action",
        label: "Action",
        width: 100,
        pinned: true,
        render: (_, row) => (
          <div className="text-center">
            <span className="cursor" onClick={() => openSummaryDialog(row)}>
              <MdOutlineRemoveRedEye size={22} />
            </span>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <ServerDataGrid
      rows={rows}
      columns={columns}
      rowId={(row) =>
        row.minimumItemType || `${row.description}-${row.dueDate}`
      }
      height="400px"
      hidePagination={true}
    />
  );
};

// WITH ACCORDION
const StudentFeeSummary = ({ paymentDetails, openSummaryDialog }) => {
  const rows = paymentDetails?.feeSummaryDTOList || [];

  return (
    <Accordion title="Student fee summary" titleColor="#262626">
      <div className="mt-3" style={{ maxWidth: "900px" }}>
        <FeeSummaryGrid rows={rows} openSummaryDialog={openSummaryDialog} />
      </div>
    </Accordion>
  );
};

export const StudentFeeSummaryWithoutAccordion = ({
  paymentDetails,
  openSummaryDialog,
}) => {
  const rows = paymentDetails?.feeSummaryDTOList || [];

  return (
    <div className="mt-3">
      <FeeSummaryGrid rows={rows} openSummaryDialog={openSummaryDialog} />
    </div>
  );
};

export default StudentFeeSummary;
